import { useState } from "react";
import { useAudit, useStores } from "../hooks/useApi";

interface Store {
  id: string;
  code: string;
  name: string;
}

interface AuditRow {
  id: string;
  storeId: string | null;
  entityType: "ENTRY" | "ITEM" | "PERIOD";
  label: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  userEmail: string;
  createdAt: string;
}

const TYPE_STYLES: Record<string, { bg: string; fg: string }> = {
  ENTRY: { bg: "#e7f1ff", fg: "#0056b3" },
  ITEM: { bg: "#fff3e0", fg: "#a06000" },
  PERIOD: { bg: "#fde8ef", fg: "#a01048" },
};

export function AdminAudit() {
  const [storeId, setStoreId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const { data: stores } = useStores();
  const { data: rows, isLoading } = useAudit({
    storeId: storeId || undefined,
    from: from || undefined,
    to: to || undefined,
    limit: 200,
  });

  const storeById = new Map(
    (((stores as Store[]) || [])).map((s) => [s.id, s.code])
  );

  const auditRows = (rows as AuditRow[]) || [];

  return (
    <div style={{ padding: "1rem" }}>
      <h1>Audit Log</h1>
      <p style={{ color: "#666", margin: "0.25rem 0 1rem" }}>
        Who changed what, newest first (latest 200 shown).
      </p>

      <div
        style={{
          display: "flex",
          gap: "1rem",
          flexWrap: "wrap",
          alignItems: "flex-end",
          marginBottom: "1.5rem",
        }}
      >
        <Filter label="Store">
          <select
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            style={inputStyle}
          >
            <option value="">All stores</option>
            {((stores as Store[]) || []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </Filter>
        <Filter label="From">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            style={inputStyle}
          />
        </Filter>
        <Filter label="To">
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            style={inputStyle}
          />
        </Filter>
      </div>

      {isLoading ? (
        <p>Loading…</p>
      ) : auditRows.length === 0 ? (
        <p style={{ color: "#666" }}>No audit entries for this filter.</p>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "4px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
            <thead style={{ backgroundColor: "#f8f9fa" }}>
              <tr>
                <th style={thStyle}>When</th>
                <th style={thStyle}>Who</th>
                <th style={thStyle}>Store</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>What</th>
                <th style={thStyle}>Field</th>
                <th style={thStyle}>Change</th>
              </tr>
            </thead>
            <tbody>
              {auditRows.map((r) => {
                const typeStyle = TYPE_STYLES[r.entityType] ?? TYPE_STYLES.ENTRY;
                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={tdStyle}>
                      {new Date(r.createdAt).toLocaleString("en-ZA", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </td>
                    <td style={tdStyle}>{r.userEmail}</td>
                    <td style={tdStyle}>
                      {r.storeId ? storeById.get(r.storeId) ?? r.storeId : "–"}
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          fontSize: "0.75rem",
                          padding: "0.15rem 0.5rem",
                          borderRadius: "10px",
                          backgroundColor: typeStyle.bg,
                          color: typeStyle.fg,
                        }}
                      >
                        {r.entityType}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{r.label}</td>
                    <td style={tdStyle}>{r.field}</td>
                    <td style={tdStyle}>
                      <span style={{ color: "#c33" }}>{r.oldValue ?? "∅"}</span>
                      {" → "}
                      <span style={{ color: "#282", fontWeight: 600 }}>
                        {r.newValue ?? "∅"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Filter({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <span style={{ fontSize: "0.875rem", color: "#666" }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle = {
  padding: "0.5rem",
  border: "1px solid #ccc",
  borderRadius: "4px",
};

const thStyle = {
  padding: "0.6rem 0.75rem",
  textAlign: "left" as const,
  fontWeight: "bold" as const,
  borderBottom: "2px solid #ddd",
};

const tdStyle = {
  padding: "0.6rem 0.75rem",
  borderBottom: "1px solid #eee",
};
